import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, FormGroup, Label, Form, Row, Col, Card, CardBody } from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { FiDollarSign, FiCalendar, FiUser, FiCheckCircle } from "react-icons/fi";

const AddStaffSalary = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [staffList, setStaffList] = useState([]);
    const [formData, setFormData] = useState({
        staff_id: "",
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        base_salary: "",
        bonus: 0,
        deduction: 0,
        payment_status: "pending",
        paid_date: "",
        payment_mode: "cash",
        note: ""
    });

    const [errors, setErrors] = useState({});

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '50px',
            borderRadius: '10px',
            borderColor: state.isFocused ? '#f16436' : '#dee2e6',
            boxShadow: 'none',
            backgroundColor: '#f8f9fa',
            '&:hover': { borderColor: '#f16436' }
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    useEffect(() => {
        fetchStaff();
    }, []);

    const fetchStaff = async () => {
        try {
            const res = await Handler({ method: "GET", url: "/staff/list.php" });
            if (res?.success) {
                // 🔥 's.s.name' la irunthu 's.staff.name' ku mathiyachu
                const formattedStaff = res.data.map(s => ({
                    value: s.id,
                    // First staff object kulla thedum, illana direct name, illana UNKNOWN
                    label: s.staff?.name?.toUpperCase() || s.name?.toUpperCase() || "UNKNOWN STAFF"
                }));

                setStaffList(formattedStaff);
            }
        } catch (err) {
            console.error("Staff fetch error:", err);
            toast.error("Error loading staff list");
        }
    };

    // 🔥 Separate Validation Logic
    const validateForm = () => {
        let formErrors = {};
        let isValid = true;

        if (!formData.staff_id) {
            formErrors.staff_id = "Please select a staff member";
            isValid = false;
        }

        if (!formData.base_salary || formData.base_salary <= 0) {
            formErrors.base_salary = "Base salary must be greater than zero";
            isValid = false;
        }

        if (formData.payment_status === "paid" && !formData.paid_date) {
            formErrors.paid_date = "Paid date is required for disbursed salaries";
            isValid = false;
        }

        setErrors(formErrors);
        if (!isValid) toast.error("Please correct the errors in the form!");
        return isValid;
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;

        // 🔥 Number fields-ku mattum Minus values block panni Round off panra logic machan
        if (type === "number") {
            let val = value === "" ? "" : Math.round(Math.abs(parseFloat(value)));
            setFormData({ ...formData, [name]: val });
        } else {
            setFormData({ ...formData, [name]: value });
        }

        if (errors[name]) setErrors({ ...errors, [name]: "" });
    };

    const handleSelectChange = (name, value) => {
        setFormData({ ...formData, [name]: value });
        if (errors[name]) setErrors({ ...errors, [name]: "" });
    };

    const netSalary = (parseFloat(formData.base_salary || 0) +
        parseFloat(formData.bonus || 0)) -
        parseFloat(formData.deduction || 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "POST",
                url: "/salary/create.php",
                data: formData
            });

            if (res?.success) {
                Swal.fire("Processed!", "Staff salary record created.", "success");
                navigate("/salary");
                dispatch(loadingFalse());
            } else {
                if (res?.errors) setErrors(res.errors);
                else toast.error(res?.message || "Internal Error");
                dispatch(loadingFalse());
            }
        } catch (err) {
            Swal.fire("Error", "Server sync failed", "error");
            dispatch(loadingFalse());
        } finally {
            dispatch(loadingFalse());
        }
    };

    // Month & Year Logic
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    const yearOptions = Array.from({ length: 3 }, (_, i) => {
        const y = currentYear - 1 + i;
        return { value: y, label: y.toString(), isDisabled: y > currentYear };
    });

    const monthOptions = Array.from({ length: 12 }, (_, i) => {
        const mVal = i + 1;
        const isFuture = (formData.year === currentYear) && (mVal > currentMonth);
        return {
            value: mVal,
            label: new Date(0, i).toLocaleString('en-US', { month: 'long' }),
            isDisabled: isFuture
        };
    });

    return (
        <div className="page-container p-3 bg-light min-vh-100">
            <div className="mb-4">
                <h3 className="fw-bold text-dark">Add Staff Salary</h3>
                <p className="text-muted small">Manage and disburse monthly employee payroll.</p>
            </div>

            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="bg-primary p-1"></div>
                <CardBody className="p-4 p-md-5">
                    <Form onSubmit={handleSubmit}>
                        <Row className="g-4">
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="small fw-bold text-uppercase text-muted"><FiUser className="me-1" /> Select Staff *</Label>
                                    <Select
                                        options={staffList}
                                        styles={customSelectStyles}
                                        placeholder="Search Staff..."
                                        onChange={(opt) => handleSelectChange("staff_id", opt?.value)}
                                        menuPortalTarget={document.body}
                                    />
                                    {errors.staff_id && <small className="text-danger">{errors.staff_id}</small>}
                                </FormGroup>
                            </Col>

                            <Col md={3}>
                                <FormGroup>
                                    <Label className="small fw-bold text-uppercase text-muted">Year</Label>
                                    <Select
                                        options={yearOptions}
                                        styles={customSelectStyles}
                                        value={yearOptions.find(y => y.value === formData.year)}
                                        onChange={(opt) => handleSelectChange("year", opt?.value)}
                                        menuPortalTarget={document.body}
                                    />
                                </FormGroup>
                            </Col>

                            <Col md={3}>
                                <FormGroup>
                                    <Label className="small fw-bold text-uppercase text-muted">Month</Label>
                                    <Select
                                        options={monthOptions}
                                        styles={customSelectStyles}
                                        value={monthOptions.find(m => m.value === formData.month)}
                                        onChange={(opt) => handleSelectChange("month", opt?.value)}
                                        menuPortalTarget={document.body}
                                    />
                                </FormGroup>
                            </Col>

                            <Col md={4}>
                                <FormGroup>
                                    <Label className="small fw-bold text-uppercase text-muted">Base Salary (₹) *</Label>
                                    <Input
                                        type="number"
                                        name="base_salary"
                                        min="0"
                                        className={`bg-light border-0 py-2 rounded-3 ${errors.base_salary ? "is-invalid" : ""}`}
                                        style={{ height: '50px' }}
                                        value={formData.base_salary}
                                        onChange={handleChange}
                                        placeholder="Positive Rounded Number"
                                    />
                                    {errors.base_salary && <small className="text-danger">{errors.base_salary}</small>}
                                </FormGroup>
                            </Col>

                            <Col md={4}>
                                <FormGroup>
                                    <Label className="small fw-bold text-uppercase text-muted">Bonus (₹)</Label>
                                    <Input
                                        type="number"
                                        name="bonus"
                                        min="0"
                                        className="bg-light border-0 py-2 rounded-3"
                                        style={{ height: '50px' }}
                                        value={formData.bonus}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>

                            <Col md={4}>
                                <FormGroup>
                                    <Label className="small fw-bold text-uppercase text-muted">Deduction (₹)</Label>
                                    <Input
                                        type="number"
                                        name="deduction"
                                        min="0"
                                        className="bg-light border-0 py-2 rounded-3"
                                        style={{ height: '50px' }}
                                        value={formData.deduction}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className="bg-light p-4 rounded-4 my-4 d-flex justify-content-between align-items-center border border-dashed border-primary">
                            <div>
                                <h6 className="text-primary text-uppercase small fw-bold mb-1">Final Net Salary</h6>
                                <p className="small text-muted m-0">Rounded positive value calculation.</p>
                            </div>
                            <h2 className="fw-bold text-dark m-0">₹ {Math.max(0, Math.round(netSalary))?.toLocaleString()}</h2>
                        </div>

                        <Row className="g-4">
                            <Col md={4}>
                                <FormGroup>
                                    <Label className="small fw-bold text-uppercase text-muted">Status</Label>
                                    <Select
                                        options={[{ value: "pending", label: "Pending" }, { value: "paid", label: "Paid" }]}
                                        styles={customSelectStyles}
                                        defaultValue={{ value: "pending", label: "Pending" }}
                                        onChange={(opt) => handleSelectChange("payment_status", opt?.value)}
                                        menuPortalTarget={document.body}
                                    />
                                </FormGroup>
                            </Col>

                            {formData.payment_status === "paid" && (
                                <>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label className="small fw-bold text-uppercase text-muted"><FiCalendar className="me-1" /> Disbursement Date *</Label>
                                            <Input
                                                type="date"
                                                name="paid_date"
                                                className={`bg-light border-0 py-2 rounded-3 ${errors.paid_date ? "is-invalid" : ""}`}
                                                style={{ height: '50px' }}
                                                value={formData.paid_date}
                                                onChange={handleChange}
                                            />
                                            {errors.paid_date && <small className="text-danger">{errors.paid_date}</small>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label className="small fw-bold text-uppercase text-muted">Payment Mode</Label>
                                            <Select
                                                options={[{ value: "cash", label: "Cash" }, { value: "bank_transfer", label: "Bank" }, { value: "upi", label: "UPI" }]}
                                                styles={customSelectStyles}
                                                defaultValue={{ value: "cash", label: "Cash" }}
                                                onChange={(opt) => handleSelectChange("payment_mode", opt?.value)}
                                                menuPortalTarget={document.body}
                                            />
                                        </FormGroup>
                                    </Col>
                                </>
                            )}
                        </Row>

                        <FormGroup className="mt-4">
                            <Label className="small fw-bold text-uppercase text-muted">Notes</Label>
                            <Input
                                type="textarea"
                                name="note"
                                value={formData.note}
                                onChange={handleChange}
                                rows="2"
                                placeholder="Any internal remarks..."
                                className="bg-light border-0 rounded-3"
                            />
                        </FormGroup>

                        <div className="mt-5 pt-4 border-top d-flex justify-content-end gap-3">
                            <Button type="button" color="white" className="px-4 py-2 rounded-3 border" onClick={() => navigate(-1)}>Cancel</Button>
                            <Button type="submit" color="primary" className="px-5 py-2 rounded-3 shadow-sm fw-bold d-flex align-items-center gap-2">
                                <FiCheckCircle /> Confirm & Create
                            </Button>
                        </div>
                    </Form>
                </CardBody>
            </Card>
        </div>
    );
};

export default AddStaffSalary;